import Card from 'components/card';
import { useEffect, useState } from 'react';
import { getProductPriceHistory } from 'interfaces/products';
import { FiTrendingUp, FiTrendingDown, FiMinus } from 'react-icons/fi';

interface PriceHistoryWidgetProps {
    productId: string;
}

interface MonthlyData {
    month: string;
    averagePrice: number | null;
    variationVsPrevious: number | null;
    variationVsCurrent: number | null;
    entryCount: number;
    key: string;
}

const PriceHistoryWidget = ({ productId }: PriceHistoryWidgetProps) => {
    const [loading, setLoading] = useState(true);
    const [priceData, setPriceData] = useState<{
        currentPurchasePrice: number;
        history: MonthlyData[];
    } | null>(null);

    useEffect(() => {
        const fetchPriceHistory = async () => {
            if (!productId) return;
            setLoading(true);
            try {
                const data = await getProductPriceHistory(productId);
                if (data) {
                    setPriceData(data);
                }
            } catch (error) {
                console.error('Erreur lors du chargement de l\'historique des prix:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPriceHistory();
    }, [productId]);

    const getVariationIcon = (variation: number | null) => {
        if (variation === null) return <FiMinus className="text-gray-400" />;
        if (variation > 0) return <FiTrendingUp className="text-red-500" />;
        if (variation < 0) return <FiTrendingDown className="text-green-500" />;
        return <FiMinus className="text-gray-400" />;
    };

    const getVariationColor = (variation: number | null) => {
        if (variation === null) return 'text-gray-400';
        if (variation > 0) return 'text-red-500';
        if (variation < 0) return 'text-green-500';
        return 'text-gray-400';
    };

    if (loading) {
        return (
            <Card extra="col-span-12 px-5 py-5 border">
                <h4 className="mb-4 text-lg font-medium">Historique des prix d'achat</h4>
                <div className="flex justify-center items-center py-10">
                    <p className="text-gray-500">Chargement...</p>
                </div>
            </Card>
        );
    }

    if (!priceData) {
        return (
            <Card extra="col-span-12 px-5 py-5 border">
                <h4 className="mb-4 text-lg font-medium">Historique des prix d'achat</h4>
                <div className="flex justify-center items-center py-10">
                    <p className="text-gray-500">Aucune donnée disponible</p>
                </div>
            </Card>
        );
    }

    const filteredHistory = priceData.history.filter(item => item.averagePrice !== null);

    return (
        <Card extra="col-span-12 px-5 py-5 border">
            <div className="mb-4 flex items-center justify-between">
                <h4 className="text-lg font-medium">Historique des prix d'achat</h4>
                <div className="text-sm text-gray-600">
                    Prix actuel: <span className="font-bold text-navy-700 dark:text-white">{priceData.currentPurchasePrice?.toFixed(2) || '0.00'} €</span>
                </div>
            </div>

            {filteredHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                    <p className="text-gray-500 mb-2">Aucun bon de commande reçu</p>
                    <p className="text-xs text-gray-400">L'historique des prix sera disponible après la réception du premier bon de commande</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-700">
                                <th className="pb-3 text-left text-xs font-bold uppercase text-gray-600 dark:text-gray-400">
                                    Mois
                                </th>
                                <th className="pb-3 text-right text-xs font-bold uppercase text-gray-600 dark:text-gray-400">
                                    Prix moyen
                                </th>
                                <th className="pb-3 text-right text-xs font-bold uppercase text-gray-600 dark:text-gray-400">
                                    vs Précédent
                                </th>
                                <th className="pb-3 text-right text-xs font-bold uppercase text-gray-600 dark:text-gray-400">
                                    vs Actuel
                                </th>
                                <th className="pb-3 text-right text-xs font-bold uppercase text-gray-600 dark:text-gray-400">
                                    Entrées
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredHistory.map((item, index) => (
                                <tr 
                                    key={item.key} 
                                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                                >
                                    <td className="py-3 text-sm font-medium text-navy-700 dark:text-white capitalize">
                                        {item.month}
                                    </td>
                                    <td className="py-3 text-right text-sm font-bold text-navy-700 dark:text-white">
                                        {item.averagePrice !== null ? `${item.averagePrice.toFixed(2)} €` : '-'}
                                    </td>
                                    <td className="py-3 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            {getVariationIcon(item.variationVsPrevious)}
                                            <span className={`text-sm font-medium ${getVariationColor(item.variationVsPrevious)}`}>
                                                {item.variationVsPrevious !== null 
                                                    ? `${item.variationVsPrevious > 0 ? '+' : ''}${item.variationVsPrevious.toFixed(2)}%` 
                                                    : '-'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="py-3 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            {getVariationIcon(item.variationVsCurrent)}
                                            <span className={`text-sm font-medium ${getVariationColor(item.variationVsCurrent)}`}>
                                                {item.variationVsCurrent !== null 
                                                    ? `${item.variationVsCurrent > 0 ? '+' : ''}${item.variationVsCurrent.toFixed(2)}%` 
                                                    : '-'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="py-3 text-right text-sm text-gray-600 dark:text-gray-400">
                                        {item.entryCount}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="mt-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3 text-xs text-blue-800 dark:text-blue-300">
                <p className="font-medium mb-1">📊 À propos de cet historique</p>
                <p className="mb-2">
                    L'historique affiche le prix d'achat moyen par mois calculé à partir des bons de commande reçus.
                </p>
                <p>
                    <strong>vs Précédent :</strong> variation par rapport au mois précédent<br />
                    <strong>vs Actuel :</strong> variation par rapport au prix d'achat actuel ({priceData.currentPurchasePrice?.toFixed(2) || '0.00'} €)
                </p>
            </div>
        </Card>
    );
};

export default PriceHistoryWidget;
